import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import Icon from '../../components/AppIcon';
import ExtrasKPICards from './components/ExtrasKPICards';
import ExtrasTable from './components/ExtrasTable';
import AddExtraModal from './components/AddExtraModal';
import DirectivesTable from './components/DirectivesTable';
import DirectiveDocumentsModal from './components/DirectiveDocumentsModal';
import DirectiveHistoryModal from './components/DirectiveHistoryModal';
import DirectiveModal from './components/DirectiveModal';
import LinkExtraModal from './components/LinkExtraModal';
import DirectiveFiltersMenu from './components/DirectiveFiltersMenu';
import DirectiveRulesHelp from './components/DirectiveRulesHelp';
import { divisionLabel } from '../../constants/divisions';
import { isPendingOverdue } from '../../utils/directiveAlerts';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useAuth } from '../../auth/AuthProvider';
import { isAdmin } from '../../utils/permissions';
import ApproveExtraModal from './components/ApproveExtraModal';
import { projectExtrasService, isCoRequired } from '../../services/projectExtrasService';
import { projectService } from '../../services/projectService';
import subcontractService from '../../services/subcontractService';
import { supabase } from '../../lib/supabase';
import useTranslation from '../../hooks/useTranslation';
import StatusFilterDropdown from '../../components/ui/StatusFilterDropdown';
import ExtraDocument from '../../pdf/ExtraDocument';
import { listClientsForProject, listProfessionalsForProject } from '../../services/projectDirectoryService';
import { getCurrentOrgName } from '../../lib/getCurrentOrgId';
import { fetchOrgLogoAsBase64 } from '../../pdf/designSystem/fetchLogo';
import organizationService from '../../services/organizationService';
import { formatCurrency as fmtCAD } from '../../utils/formatCurrency';
import BulkActionBar from '../../components/ui/BulkActionBar';
import {
  listDirectives,
  countDirectiveFiles,
  listDirectiveDocumentCounts,
  createDirective,
  uploadDirectiveFile,
  markNoImpact,
  createOutcomeExtra,
  updateDirective,
  setDirectiveOutcome,
  deleteDirective,
  getExtraRemovalCheck,
  revertDirectiveDeletingExtra,
} from '../../services/projectDirectivesService';
import { signedExtraAmount as signedAmount } from '../../utils/extraSign';
import { upload as uploadEntityDocument } from '../../services/entityDocumentsService';

const ALLOWED_SORT_KEYS = ['title', 'type', 'amount', 'status', 'created_at', 'co_number', 'directive_nomenclature', 'division', 'received_date', 'cost_accepted', 'category'];
const ALLOWED_SORT_DIRS = ['asc', 'desc'];

const LS_KEY = 'extras_table_state';

const loadSavedTableState = () => {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    // ignore parse errors
  }
  return null;
};

const saveTableState = (state) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
};

// La VERITE de « accepte » vit dans ExtrasTable.jsx:482 : un montant accepte
// n'existe que sur un extra APPROUVE. Sur un brouillon, la valeur en base est un
// residu (l'extra a ete approuve puis retrograde) et ne doit pas s'exporter.
// Un extra approuve dont le montant vaut 0 exporte bien 0.00 : c'est ce que
// l'ecran affiche.
const hasAcceptedAmount = (e) => e?.status === 'approved' && e?.costAccepted != null;

// L'ecran affiche le montant soumis PROFIT INCLUS (ExtrasTable.jsx:473-475), et
// ExtraDocument aussi. Les exports etaient les seuls a l'omettre.
const submittedWithProfit = (e) =>
  signedAmount(e, (parseFloat(e?.costSubmitted ?? e?.amount) || 0) + (parseFloat(e?.profit) || 0));

// ─── CSV Export ───────────────────────────────────────────────────────────────
const EXPORT_COLUMNS = (t) => [
  t('extras.export.columns.type'),
  t('extras.export.columns.coNumber'),
  t('extras.export.columns.description'),
  t('extras.export.columns.submitted'),
  t('extras.export.columns.accepted'),
  t('extras.export.columns.status'),
];

const exportToCSV = (rows, projectName, label, t) => {
  const STATUS_LABELS = {
    draft:     t('status.draft'),
    submitted: t('status.submitted'),
    approved:  t('status.approved'),
    rejected:  t('status.rejected'),
  };
  const headers = EXPORT_COLUMNS(t);
  const fmtNum = (val) => {
    const num = parseFloat(val) || 0;
    return num?.toFixed(2);
  };
  const csvRows = [
    headers?.map(h => `"${h}"`)?.join(','),
    ...rows?.map(e => [
      `"${(e?.type || '')?.replace(/"/g, '""')}"`,`"${(e?.coNumber || '')?.replace(/"/g, '""')}"`,
      `"${(e?.title || '')?.replace(/"/g, '""')}"`,fmtNum(submittedWithProfit(e)),(hasAcceptedAmount(e) ? fmtNum(signedAmount(e, e?.costAccepted)) : ''),`"${(STATUS_LABELS?.[e?.status] || e?.status || '')?.replace(/"/g, '""')}"`
    ]?.join(','))
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `extras-credits${label ? '-' + label : ''}${projectName ? '-' + projectName?.replace(/\s+/g, '_') : ''}_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
  document?.body?.appendChild(a);
  a?.click();
  document?.body?.removeChild(a);
  URL.revokeObjectURL(url);
};

// ─── PDF Export ───────────────────────────────────────────────────────────────
const exportToPDF = (rows, projectName, label, t, language) => {
  const STATUS_CONFIG = {
    draft:     { label: t('status.draft'),     bg: '#f3f4f6', color: '#4b5563' },
    submitted: { label: t('status.submitted'), bg: '#dbeafe', color: '#1d4ed8' },
    approved:  { label: t('status.approved'),  bg: '#dcfce7', color: '#16a34a' },
    rejected:  { label: t('status.rejected'),  bg: '#fee2e2', color: '#b91c1c' },
  };

  const fmtAmt = (val) => fmtCAD(val);

  const tableRows = rows?.map(e => {
    const cfg = STATUS_CONFIG?.[e?.status] || STATUS_CONFIG?.draft;
    const badge = `<span style="display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:${cfg?.bg};color:${cfg?.color}">${cfg?.label}</span>`;
    return `
    <tr>
      <td>${e?.type || ''}</td>
      <td>${e?.coNumber || '—'}</td>
      <td>${e?.title || ''}</td>
      <td style="text-align:right">${fmtAmt(submittedWithProfit(e))}</td>
      <td style="text-align:right">${hasAcceptedAmount(e) ? fmtAmt(signedAmount(e, e?.costAccepted)) : ''}</td>
      <td>${badge}</td>
    </tr>
  `;
  })?.join('');

  // Soumis : TOUTES les lignes visibles — un brouillon a bel et bien ete chiffre.
  const totalSubmitted = rows?.reduce((s, e) => s + submittedWithProfit(e), 0);
  // Accepte : les lignes APPROUVEES seulement. Les deux totaux ne couvrent donc
  // pas la meme population — la note sous le tableau le dit au lecteur.
  const totalAccepted = rows
    ?.filter(hasAcceptedAmount)
    ?.reduce((s, e) => s + signedAmount(e, e?.costAccepted), 0);

  const exportTitle = t('extras.export.title');
  const title = label ? `${exportTitle} — ${label}` : exportTitle;
  const [colType, colCo, colDesc, colSubmitted, colAccepted, colStatus] = EXPORT_COLUMNS(t);
  const exportedOn = t('extras.export.exportedOn', {
    date: new Date()?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { month: 'long', day: 'numeric', year: 'numeric' })
  });

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; margin: 32px; }
    h1 { font-size: 16px; margin-bottom: 4px; }
    .sub { color: #666; font-size: 11px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f3f4f6; text-align: left; padding: 6px 10px; font-size: 10px; color: #555; border-bottom: 1px solid #e5e7eb; }
    td { padding: 6px 10px; border-bottom: 1px solid #f3f4f6; }
    tfoot td { font-weight: bold; background: #f9fafb; border-top: 2px solid #e5e7eb; }
    .note { color: #666; font-size: 10px; margin-top: 8px; }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <div class="sub">${projectName || ''} &mdash; ${exportedOn}</div>
  <table>
    <thead>
      <tr>
        <th>${colType}</th><th>${colCo}</th><th>${colDesc}</th>
        <th style="text-align:right">${colSubmitted}</th>
        <th style="text-align:right">${colAccepted}</th>
        <th>${colStatus}</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
    <tfoot>
      <tr>
        <td colspan="3">${t('extras.export.totalItems', { count: rows?.length })}</td>
        <td style="text-align:right">${fmtAmt(totalSubmitted)}</td>
        <td style="text-align:right">${fmtAmt(totalAccepted)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
  <div class="note">${t('extra.exportAcceptedNote')}</div>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win?.document?.write(html);
    win?.document?.close();
    win?.focus();
    setTimeout(() => { win?.print(); }, 400);
  }
};

// ─── Bulk Action Bar ──────────────────────────────────────────────────────────
const ActionFeedbackToast = ({ feedback }) => {
  const { t } = useTranslation();
  if (!feedback) return null;

  const configs = {
    approved:  { icon: 'CheckCircle2', text: t('status.approved'),  bg: 'bg-green-600' },
    submitted: { icon: 'Send',         text: t('status.submitted'), bg: 'bg-blue-600'  },
    to_resubmit: { icon: 'RotateCcw',  text: t('status.to_resubmit'), bg: 'bg-amber-600' },
    rejected:  { icon: 'XCircle',      text: t('status.rejected'),  bg: 'bg-red-500'   },
    draft:     { icon: 'FileEdit',     text: t('status.draft'),     bg: 'bg-gray-600'  },
  };

  const cfg = configs?.[feedback] || configs?.approved;

  return (
    <div
      className={`fixed bottom-6 left-1/2 z-50 ${cfg?.bg} text-white px-4 py-2 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium`}
      style={{
        transform: 'translateX(-50%)',
        animation: 'feedbackSlideUp 0.25s ease-out forwards',
      }}
    >
      <Icon name={cfg?.icon} size={15} />
      {cfg?.text}
      <style>{`
        @keyframes feedbackSlideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(10px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

// Onglets de la page, dans l'URL (?tab=).
const PAGE_TABS = ['extras', 'directives'];

// Filtres de l'onglet Directives, dans l'URL comme ?tab= (prefixe « d » pour ne
// pas croiser les parametres de l'onglet Extras).
const DIRECTIVE_FILTER_PARAMS = {
  status: 'dstatus',
  professional: 'dpro',
  division: 'ddiv',
  from: 'dfrom',
  to: 'dto',
};
const DIRECTIVE_STATUSES = ['pending', 'overdue', 'extra', 'credit', 'no_impact'];

const ExtrasPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language } = useTranslation();

  const initialProject = location?.state?.project;

  const getInitialSort = () => {
    // 1. URL params take priority
    const sortKey = searchParams?.get('sort');
    const sortDir = searchParams?.get('dir');
    if (ALLOWED_SORT_KEYS?.includes(sortKey) && ALLOWED_SORT_DIRS?.includes(sortDir)) {
      return { key: sortKey, direction: sortDir };
    }
    // 2. Saved localStorage state
    const saved = loadSavedTableState();
    if (saved?.sortColumn && ALLOWED_SORT_KEYS?.includes(saved?.sortColumn) && ALLOWED_SORT_DIRS?.includes(saved?.sortDirection)) {
      return { key: saved?.sortColumn, direction: saved?.sortDirection };
    }
    return { key: 'created_at', direction: 'asc' };
  };

  const getInitialSearch = () => {
    const saved = loadSavedTableState();
    return saved?.search || '';
  };

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(initialProject || null);
  const [extras, setExtras] = useState([]);
  const [sortConfig, setSortConfig] = useState(getInitialSort);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  // Fiche ouverte en consultation (clic sur la ligne) plutot qu'en modification.
  const [editingStartsInView, setEditingStartsInView] = useState(false);
  const [subcontracts, setSubcontracts] = useState([]);
  const [searchQuery, setSearchQuery] = useState(getInitialSearch);
  const [statusFilter, setStatusFilter] = useState('all');

  // Status filter options for the Extras tab (project_extras.status)
  const extraStatusFilterOptions = [
    { value: 'all', label: t('extras.toolbar.allStatuses'), dot: null },
    { value: 'draft', label: t('status.draft'), dot: '#9ca3af' },
    { value: 'submitted', label: t('status.submitted'), dot: '#1d4ed8' },
    { value: 'to_resubmit', label: t('status.to_resubmit'), dot: '#F59E0B' },
    { value: 'approved', label: t('status.approved'), dot: '#16a34a' },
    { value: 'rejected', label: t('status.rejected'), dot: '#b91c1c' },
    // Ordre de changement du client (extras approuves seulement)
    { value: 'co_pending', label: t('extras.co.filterPending'), dot: '#F59E0B' },
    { value: 'co_received', label: t('extras.co.filterReceived'), dot: '#22C55E' },
  ];
  // Approval cost modal state
  const [approvalModal, setApprovalModal] = useState(null); // { extraId, previousStatus }
  const [approvalCost, setApprovalCost] = useState('0');
  const [approvalSaving, setApprovalSaving] = useState(false);
  // ApproveExtraModal state
  const [approveExtraModal, setApproveExtraModal] = useState(null); // { extra, lines }
  const [approveExtraLoading, setApproveExtraLoading] = useState(false);

  // ─── Selection state ───────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [actionFeedback, setActionFeedback] = useState(null);
  const feedbackTimerRef = useRef(null);
  const [orgName, setOrgName] = useState('');

  // ─── Onglet Directives ─────────────────────────────────────────────────────
  // L'onglet vit dans l'URL (?tab=), comme le tri : il survit au rechargement.
  const tabFromUrl = searchParams?.get('tab');
  const [activeTab, setActiveTabState] = useState(PAGE_TABS?.includes(tabFromUrl) ? tabFromUrl : 'extras');
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const [directives, setDirectives] = useState([]);
  const [directiveSearch, setDirectiveSearch] = useState('');
  const [directiveBusyId, setDirectiveBusyId] = useState(null);
  const [highlightDirectiveId, setHighlightDirectiveId] = useState(null);
  const [directiveDocs, setDirectiveDocs] = useState(null);
  // Nombre de pieces par directive, lu en UNE requete avec la liste
  // (directive_documents), puis tenu a jour localement. Si cette lecture
  // echoue, repli : un comptage Storage par directive (undefined = en cours,
  // null = indisponible).
  const [directiveDocCounts, setDirectiveDocCounts] = useState({});
  const [docCountsFromDb, setDocCountsFromDb] = useState(false);
  const [docCountsFallback, setDocCountsFallback] = useState(false);
  const countingRef = useRef(new Set());
  // Lot D2 : modifier, changer l'issue, historique, supprimer (admin).
  const { userProfile } = useAuth();
  const canDeleteDirective = isAdmin(userProfile);
  const [outcomeChange, setOutcomeChange] = useState(null); // { directive, outcome }
  const [outcomeSaving, setOutcomeSaving] = useState(false);
  const [historyDirective, setHistoryDirective] = useState(null);
  const [deletingDirective, setDeletingDirective] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  // Lot D3c : une seule modale de directive, { mode: 'view'|'edit'|'create', id }.
  // On garde l'id : la modale lit toujours la version a jour de la liste.
  const [directiveModal, setDirectiveModal] = useState(null);
  // Lot D5b : relier a un extra existant / delier.
  const [linkingDirective, setLinkingDirective] = useState(null);
  const [unlinkingDirective, setUnlinkingDirective] = useState(null);
  const [unlinkSaving, setUnlinkSaving] = useState(false);
  // Lot D9 : « Revenir a A evaluer » d'une directive liee -> { directive, check }.
  const [revertExtra, setRevertExtra] = useState(null);
  const [revertSaving, setRevertSaving] = useState(false);
  const [directiveToast, setDirectiveToast] = useState(null);
  const directiveToastRef = useRef(null);

  const showDirectiveToast = (type, text) => {
    clearTimeout(directiveToastRef?.current);
    setDirectiveToast({ type, text });
    directiveToastRef.current = setTimeout(() => setDirectiveToast(null), 4000);
  };

  useEffect(() => () => clearTimeout(directiveToastRef?.current), []);

  useEffect(() => {
    getCurrentOrgName()?.then(name => {
      if (name) setOrgName(name);
    });
  }, []);

  const showFeedback = (type) => {
    clearTimeout(feedbackTimerRef?.current);
    setActionFeedback(type);
    feedbackTimerRef.current = setTimeout(() => setActionFeedback(null), 2500);
  };

  useEffect(() => {
    return () => clearTimeout(feedbackTimerRef?.current);
  }, []);

  const handleToggleRow = (extraId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(extraId)) next?.delete(extraId);
      else next?.add(extraId);
      return next;
    });
  };

  const handleToggleAll = (allIds, allSelected) => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  // ─── Bulk status update ────────────────────────────────────────────────────
  const handleBulkStatusChange = async (newStatus) => {
    // ═══════════════════════════════════════════════════════════════════════
    // 🔴 NE JAMAIS RETIRER CETTE GARDE.
    //
    // Approuver un extra doit TOUJOURS passer par la modale de cout puis par
    // ApproveExtraModal, qui cree les avenants AVANT de changer le statut.
    // Aucun chemin en lot ne doit ecrire 'approved' directement.
    //
    // La branche `else` plus bas appelle updateStatus() sans creer le moindre
    // avenant : un extra approuve par la sortirait sans avenant, sans titre et
    // sans description. Aucune entree du menu ne passe 'approved' aujourd'hui,
    // mais il suffirait d'en ajouter une pour rouvrir le trou.
    // ═══════════════════════════════════════════════════════════════════════
    if (newStatus === 'approved') {
      console.warn("handleBulkStatusChange: 'approved' refuse — passer par ApproveExtraModal");
      return;
    }

    if (selectedIds?.size === 0) return;

    try {
      const ids = Array.from(selectedIds);

      if (newStatus !== 'approved') {
        // Update status only in DB for all selected extras — via le service,
        // qui remet aussi l'ODC a zero dans le meme UPDATE.
        await Promise.all(
          ids?.map(extraId => projectExtrasService?.updateStatus(extraId, newStatus))
        );
        // Optimistic local state update
        setExtras(prev =>
          prev?.map(extra =>
            selectedIds?.has(extra?.id)
              ? { ...extra, status: newStatus, coStatus: null, coReceivedAt: null, coReceivedBy: null }
              : extra
          )
        );
      } else {
        await Promise.all(ids?.map(extraId => projectExtrasService?.updateStatus(extraId, newStatus)));
        await fetchData({ silent: true });
      }

      setSelectedIds(new Set());
      showFeedback(newStatus);
    } catch (err) {
      console.error('Error bulk updating status:', err);
      await fetchData({ silent: true });
    }
  };

  // Une directive porte au plus un extra : la table des extras n'a besoin que
  // de l'origine, resolue ici plutot que par une seconde requete.
  const directiveMap = React.useMemo(() => {
    const map = {};
    (directives || [])?.forEach((d) => { map[d?.id] = d; });
    return map;
  }, [directives]);

  // ─── Export helpers ────────────────────────────────────────────────────────
  // Sort extras
  const sortedExtras = [...(extras || [])]?.sort((a, b) => {
    if (!sortConfig?.key) return 0;
    let aVal, bVal;
    switch (sortConfig?.key) {
      case 'title': aVal = a?.title?.toLowerCase() || ''; bVal = b?.title?.toLowerCase() || ''; break;
      case 'type': aVal = a?.type || ''; bVal = b?.type || ''; break;
      case 'amount': aVal = parseFloat(a?.amount) || 0; bVal = parseFloat(b?.amount) || 0; break;
      case 'status': aVal = a?.status || ''; bVal = b?.status || ''; break;
      case 'created_at': aVal = new Date(a?.createdAt)?.getTime() || 0; bVal = new Date(b?.createdAt)?.getTime() || 0; break;
      // Plus aucun en-tete n'emet cette cle, mais getInitialSort la relit depuis
      // l'URL ou le localStorage : un tri enregistre avant ce lot doit continuer
      // de trier, pas retomber silencieusement dans le `default`.
      case 'co_number': aVal = a?.coNumber?.toLowerCase() || ''; bVal = b?.coNumber?.toLowerCase() || ''; break;
      // La colonne O.C. affiche desormais la directive liee : elle trie sur ce
      // qu'elle montre, pas sur co_number qui n'y figure plus.
      case 'directive_nomenclature': {
        const aDir = a?.directiveId ? directiveMap?.[a?.directiveId] : null;
        const bDir = b?.directiveId ? directiveMap?.[b?.directiveId] : null;
        const aNom = aDir?.nomenclature || '';
        const bNom = bDir?.nomenclature || '';
        // Un extra sans directive part en dernier, quel que soit le sens du tri.
        if (!aNom && !bNom) return 0;
        if (!aNom) return 1;
        if (!bNom) return -1;
        aVal = aNom?.toLowerCase();
        bVal = bNom?.toLowerCase();
        break;
      }
      // La cellule Origine montre la division de la DIRECTIVE, pas celle de
      // l'extra : les deux divergent des qu'on modifie l'extra a la main.
      case 'division': {
        const aDir = a?.directiveId ? directiveMap?.[a?.directiveId] : null;
        const bDir = b?.directiveId ? directiveMap?.[b?.directiveId] : null;
        aVal = (aDir?.division || '')?.toLowerCase();
        bVal = (bDir?.division || '')?.toLowerCase();
        break;
      }
      case 'received_date': aVal = new Date(a?.receivedDate)?.getTime() || 0; bVal = new Date(b?.receivedDate)?.getTime() || 0; break;
      case 'cost_accepted': aVal = parseFloat(a?.costAccepted) || 0; bVal = parseFloat(b?.costAccepted) || 0; break;
      case 'category': {
        // NULL values sort last
        const aNull = !a?.category;
        const bNull = !b?.category;
        if (aNull && bNull) return 0;
        if (aNull) return 1;
        if (bNull) return -1;
        aVal = a?.category?.toLowerCase() || '';
        bVal = b?.category?.toLowerCase() || '';
        break;
      }
      default: return 0;
    }
    if (aVal < bVal) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const statusFilteredExtras = statusFilter === 'all'
    ? sortedExtras
    : statusFilter === 'co_pending' || statusFilter === 'co_received'
      ? sortedExtras?.filter(e => e?.status === 'approved' && isCoRequired(e?.category)
          && (statusFilter === 'co_received' ? e?.coStatus === 'received' : e?.coStatus !== 'received'))
      : sortedExtras?.filter(e => (e?.status || 'draft') === statusFilter);

  const filteredExtras = searchQuery?.trim()
    ? statusFilteredExtras?.filter(e => {
        const q = searchQuery?.toLowerCase()?.trim();
        const title = e?.title?.toLowerCase() || '';
        const coNumber = e?.coNumber?.toLowerCase() || '';
        const division = e?.division?.toLowerCase() || '';
        const type = e?.type?.toLowerCase() || '';
        const receivedDate = e?.receivedDate ? String(e?.receivedDate)?.toLowerCase() : '';
        // La nomenclature est desormais a l'ecran : on doit pouvoir la chercher.
        // coNumber reste cherchable — il vit encore dans le PDF, le CSV, le nom
        // du fichier et les listes de demande de paiement.
        const nomenclature = (e?.directiveId ? directiveMap?.[e?.directiveId]?.nomenclature : '')?.toLowerCase() || '';
        return title?.includes(q) || coNumber?.includes(q) || nomenclature?.includes(q) || division?.includes(q) || type?.includes(q) || receivedDate?.includes(q);
      })
    : statusFilteredExtras;

  const handleExportCSVFinal = () => {
    if (selectedIds?.size > 0) {
      const rows = filteredExtras?.filter(e => selectedIds?.has(e?.id)) || [];
      exportToCSV(rows, project?.name, 'selected', t);
    } else {
      exportToCSV(filteredExtras || [], project?.name, null, t);
    }
  };

  const handleExportPDFFinal = () => {
    if (selectedIds?.size > 0) {
      const rows = filteredExtras?.filter(e => selectedIds?.has(e?.id)) || [];
      exportToPDF(rows, project?.name, t('extras.export.selectedLabel', { count: rows?.length }), t, language);
    } else {
      exportToPDF(filteredExtras || [], project?.name, null, t, language);
    }
  };

  // Persist table state to localStorage whenever relevant state changes
  useEffect(() => {
    saveTableState({
      search: searchQuery,
      sortColumn: sortConfig?.key,
      sortDirection: sortConfig?.direction,
      filters: {},
      page: 1
    });
  }, [searchQuery, sortConfig]);

  // Write default sort to URL on mount if missing
  useEffect(() => {
    const sortKey = searchParams?.get('sort');
    const sortDir = searchParams?.get('dir');
    const isValid = ALLOWED_SORT_KEYS?.includes(sortKey) && ALLOWED_SORT_DIRS?.includes(sortDir);
    if (!isValid) {
      // Fusion : tab et highlight restent dans l'URL.
      const next = new URLSearchParams(searchParams);
      next.set('sort', sortConfig?.key || 'created_at');
      next.set('dir', sortConfig?.direction || 'asc');
      setSearchParams(next, { replace: true });
    }
  }, []);

  // Sync sortConfig from URL on back/forward
  useEffect(() => {
    const sortKey = searchParams?.get('sort');
    const sortDir = searchParams?.get('dir');
    if (ALLOWED_SORT_KEYS?.includes(sortKey) && ALLOWED_SORT_DIRS?.includes(sortDir)) {
      setSortConfig(prev => {
        if (prev?.key === sortKey && prev?.direction === sortDir) return prev;
        return { key: sortKey, direction: sortDir };
      });
    }
    // Onglet : suit l'URL (retour arriere / avant).
    const tab = searchParams?.get('tab');
    if (PAGE_TABS?.includes(tab)) {
      setActiveTabState((prev) => (prev === tab ? prev : tab));
    }
  }, [searchParams]);

  useEffect(() => {
    if (id) {
      fetchData();
      if (!initialProject) {
        projectService?.getById(id)?.then(data => setProject(data))?.catch(err => console.error('Error fetching project:', err));
      }
      subcontractService?.getByProjectId(id)
        ?.then(data => setSubcontracts(data || []))
        ?.catch(err => console.error('Error fetching subcontracts:', err));
      loadDirectivesWithCounts();
    }
  }, [id]);

  // Re-fetch extras whenever the Extras view is (re)entered so that avenants
  // signed on the Avenants page are reflected as a lock when the user comes back.
  // Pas au montage : l'effet [id] ci-dessus vient deja de charger (double appel).
  const pathnameSeenRef = useRef(false);
  useEffect(() => {
    if (!pathnameSeenRef.current) { pathnameSeenRef.current = true; return; }
    if (id) {
      fetchData({ silent: true });
    }
  }, [location?.pathname]);

  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await projectExtrasService?.getByProjectId(id);
      setExtras(data || []);
    } catch (err) {
      console.error('Error fetching extras:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // ─── Directives ────────────────────────────────────────────────────────────
  const fetchDirectives = async () => {
    const rows = await listDirectives(id);
    setDirectives(rows || []);
  };

  // Premier chargement : la liste et les compteurs de pieces ensemble, sans
  // comptage par ligne.
  const loadDirectivesWithCounts = async () => {
    const [rows, counts] = await Promise.all([listDirectives(id), listDirectiveDocumentCounts(id)]);
    if (counts) {
      setDirectiveDocCounts(counts);
      setDocCountsFromDb(true);
    } else {
      setDocCountsFallback(true);
    }
    setDirectives(rows || []);
  };

  const handleCreateDirective = async ({ files, ...form }) => {
    const created = await createDirective({
      projectId: id,
      nomenclature: form?.nomenclature,
      division: form?.division,
      professionalId: form?.professionalId,
      title: form?.title,
      description: form?.description,
      dateDirective: form?.dateDirective,
      rfiId: form?.rfiId || null,
    });
    // Les pieces jointes attendaient l'id : elles partent maintenant. Un
    // televersement rate ne perd pas la directive, deja enregistree.
    let uploaded = 0;
    for (const file of files || []) {
      try {
        await uploadDirectiveFile(created?.id, file, id);
        uploaded += 1;
      } catch (err) {
        console.error('[directives] piece jointe non televersee:', file?.name, err);
      }
    }
    // Compteur de la nouvelle directive : connu, sans requete.
    if (created?.id) setDirectiveDocCounts((prev) => ({ ...prev, [created?.id]: uploaded }));
    await fetchDirectives();
    setHighlightDirectiveId(created?.id || null);
  };

  const handleDirectiveOutcome = async (directive, type) => {
    if (directiveBusyId) return;
    setDirectiveBusyId(directive?.id);
    try {
      if (type === 'no_impact') {
        await markNoImpact(directive?.id);
        showDirectiveToast('success', t('extras.directives.noImpactToast'));
      } else {
        await createOutcomeExtra({ directive, type, projectId: id });
        showDirectiveToast(
          'success',
          type === 'extra'
            ? t('extras.directives.extraCreatedToast')
            : t('extras.directives.creditCreatedToast')
        );
        await fetchData({ silent: true });
      }
      await fetchDirectives();
    } catch (err) {
      console.error('[directives] issue non enregistree:', err);
      if (err?.message === 'DIRECTIVE_LINK_FAILED_EXTRA_KEPT') {
        // L'extra existe mais n'a pas pu etre rattache, et le retrait a echoue :
        // on le dit plutot que de laisser un orphelin silencieux.
        showDirectiveToast('error', t('extras.directives.orphanNotice', {
          number: err?.extra?.co_number || err?.extra?.id || '—',
        }));
        await fetchData({ silent: true });
      } else if (err?.message === 'DIRECTIVE_LINK_FAILED_EXTRA_ROLLED_BACK') {
        showDirectiveToast('error', t('extras.directives.rollbackNotice'));
      } else {
        showDirectiveToast('error', t('extras.directives.outcomeError'));
      }
    } finally {
      setDirectiveBusyId(null);
    }
  };

  // ─── Lot D2 ─────────────────────────────────────────────────────────────────
  const handleUpdateDirective = async (directiveId, form) => {
    await updateDirective(directiveId, {
      nomenclature: form?.nomenclature,
      title: form?.title,
      description: form?.description,
      division: form?.division,
      date_directive: form?.dateDirective,
      professional_id: form?.professionalId,
      rfi_id: form?.rfiId || null,
    });
    await fetchDirectives();
    showDirectiveToast('success', t('extras.directives.updatedToast'));
  };

  // Texte de confirmation : ce que le changement d'issue va faire, concretement.
  const outcomeChangeMessage = (change) => {
    const directive = change?.directive;
    const outcome = change?.outcome;
    const linked = directive?.extra_id
      ? t('extras.directives.outcomeChange.detach', { number: directive?.extra?.co_number || t('extras.directives.outcomeChange.linkedExtra') })
      : '';
    const effect = outcome === 'extra' || outcome === 'credit'
      ? t('extras.directives.outcomeChange.create', {
          type: t(outcome === 'extra' ? 'extras.directives.outcomeChange.typeExtra' : 'extras.directives.outcomeChange.typeCredit'),
        })
      : t('extras.directives.outcomeChange.toLabel', { label: t(`extras.directives.outcome.${outcome}`) });
    return [linked, effect]?.filter(Boolean)?.join(' ');
  };

  const handleConfirmOutcomeChange = async () => {
    if (!outcomeChange || outcomeSaving) return;
    const { directive, outcome } = outcomeChange;
    setOutcomeSaving(true);
    try {
      await setDirectiveOutcome({ directive, outcome, projectId: id });
      showDirectiveToast('success', t('extras.directives.outcomeChangedToast'));
    } catch (err) {
      console.error('[directives] changement d\'issue:', err);
      if (err?.message === 'EXTRA_DETACH_FAILED') {
        showDirectiveToast('error', t('extras.directives.detachError', { number: directive?.extra?.co_number || '—' }));
      } else if (err?.message === 'DIRECTIVE_LINK_FAILED_EXTRA_KEPT') {
        showDirectiveToast('error', t('extras.directives.orphanNotice', { number: err?.extra?.co_number || err?.extra?.id || '—' }));
      } else if (err?.message === 'DIRECTIVE_LINK_FAILED_EXTRA_ROLLED_BACK') {
        showDirectiveToast('error', t('extras.directives.rollbackNotice'));
      } else {
        showDirectiveToast('error', t('extras.directives.outcomeError'));
      }
    } finally {
      setOutcomeSaving(false);
      setOutcomeChange(null);
      await fetchDirectives();
      // Un extra a pu etre cree ou detache : l'onglet Extras suit.
      await fetchData({ silent: true });
    }
  };

  const handleRequestDelete = (directive) => {
    if (directive?.extra_id) {
      showDirectiveToast('error', t('extras.directives.delete.linkedExtra'));
      return;
    }
    setDeletingDirective(directive);
  };

  const handleConfirmDelete = async () => {
    if (!deletingDirective || deleteSaving) return;
    setDeleteSaving(true);
    try {
      await deleteDirective(deletingDirective);
      setDirectiveDocCounts((prev) => {
        const next = { ...prev };
        delete next[deletingDirective?.id];
        return next;
      });
      showDirectiveToast('success', t('extras.directives.deletedToast'));
    } catch (err) {
      console.error('[directives] suppression:', err);
      showDirectiveToast(
        'error',
        err?.message === 'LINKED_EXTRA' ? t('extras.directives.delete.linkedExtra') : t('extras.directives.delete.error')
      );
    } finally {
      setDeleteSaving(false);
      setDeletingDirective(null);
      await fetchDirectives();
    }
  };

  // Relier : la modale a pose le lien ; directives ET extras sont relus (la
  // colonne O.C. de l'onglet Extras & Credits suit).
  const handleExtraLinked = async (extra) => {
    await fetchDirectives();
    await fetchData({ silent: true });
    // Le toast nomme l'extra reellement relie.
    const label = [extra?.co_number, extra?.title]?.filter(Boolean)?.join(' · ') || '—';
    showDirectiveToast('success', t('extras.directives.link.linkedToast', { label }));
  };

  // Delier = issue « A evaluer » (setDirectiveOutcome) : l'extra est conserve.
  const handleConfirmUnlink = async () => {
    if (!unlinkingDirective || unlinkSaving) return;
    setUnlinkSaving(true);
    try {
      await setDirectiveOutcome({ directive: unlinkingDirective, outcome: 'pending', projectId: id });
      showDirectiveToast('success', t('extras.directives.link.unlinkedToast'));
    } catch (err) {
      console.error('[directives] deliaison:', err);
      showDirectiveToast(
        'error',
        err?.message === 'EXTRA_DETACH_FAILED'
          ? t('extras.directives.detachError', { number: unlinkingDirective?.extra?.co_number || '—' })
          : t('extras.directives.link.unlinkError')
      );
    } finally {
      setUnlinkSaving(false);
      setUnlinkingDirective(null);
      await fetchDirectives();
      await fetchData({ silent: true });
    }
  };

  // ─── Lot D9 : revenir a « A evaluer » selon le statut de l'extra lie ─────────
  // Lecture d'abord (aucune ecriture) : la boite affichee depend du resultat.
  const handleRequestRevertExtra = async (directive) => {
    try {
      const check = await getExtraRemovalCheck(directive?.extra_id);
      setRevertExtra({ directive, check });
    } catch (err) {
      console.error('[directives] verification de l\'extra:', err);
      showDirectiveToast('error', t('extras.directives.issue.checkError'));
    }
  };

  // « n° · titre · montant · statut » de l'extra lie.
  const revertExtraDetails = (extra) => {
    if (!extra) return '—';
    const accepted = extra?.status === 'approved' && extra?.cost_accepted != null ? parseFloat(extra?.cost_accepted) || 0 : null;
    const amount = accepted ?? ((parseFloat(extra?.cost_submitted ?? extra?.amount) || 0) + (parseFloat(extra?.profit) || 0));
    return [
      extra?.co_number || '—',
      extra?.title,
      fmtCAD(extra?.type === 'credit' ? -Math.abs(amount) : amount),
      t(`status.${extra?.status}`),
    ]?.filter(Boolean)?.join(' · ');
  };

  // Raisons d'un blocage par references (facture ST, DP, depense, avenant).
  const revertBlockReasons = (references) =>
    Object.entries(references || {})
      ?.filter(([, n]) => n > 0)
      ?.map(([key, n]) => t(`extras.directives.issue.reason.${key}`, { count: n }))
      ?.join(' ; ');

  const revertDialog = (() => {
    const check = revertExtra?.check;
    const number = check?.extra?.co_number || '—';
    if (!check) return { title: '', message: '' };
    if (check?.mode === 'blocked') {
      return {
        title: t('extras.directives.issue.revertBlockedTitle'),
        message: check?.approved
          ? t('extras.directives.issue.revertBlockedApproved', { details: revertExtraDetails(check?.extra) })
          : t('extras.directives.issue.revertBlockedRefs', { number, reasons: revertBlockReasons(check?.references) }),
      };
    }
    if (check?.mode === 'draft') {
      return {
        title: t('extras.directives.issue.backToPending'),
        message: t('extras.directives.issue.revertDraftMessage', { number }),
        confirmLabel: t('extras.directives.issue.backToPending'),
      };
    }
    return {
      title: t('extras.directives.issue.revertSubmittedTitle'),
      message: t('extras.directives.issue.revertSubmittedMessage', { details: revertExtraDetails(check?.extra) }),
      confirmLabel: t('extras.directives.issue.revertDeleteConfirm'),
    };
  })();

  const handleConfirmRevertExtra = async () => {
    if (!revertExtra || revertSaving) return;
    const { directive, check } = revertExtra;
    setRevertSaving(true);
    try {
      await revertDirectiveDeletingExtra({ directive });
      showDirectiveToast('success', t('extras.directives.issue.revertedToast', { number: check?.extra?.co_number || '—' }));
      setRevertExtra(null);
    } catch (err) {
      console.error('[directives] retour a A evaluer:', err);
      if (err?.message === 'EXTRA_REMOVAL_BLOCKED') {
        // L'extra a change depuis l'ouverture : la boite passe en blocage.
        setRevertExtra({ directive, check: err?.check });
      } else {
        showDirectiveToast(
          'error',
          err?.message === 'DIRECTIVE_REVERT_FAILED'
            ? t('extras.directives.issue.revertError')
            : err?.message === 'EXTRA_DELETE_FAILED' && err?.restored
              ? t('extras.directives.issue.deleteFailed')
              : err?.message === 'EXTRA_DELETE_FAILED'
                ? t('extras.directives.issue.deleteFailedNoRestore')
                : t('extras.directives.issue.revertError')
        );
        setRevertExtra(null);
      }
    } finally {
      setRevertSaving(false);
      await fetchDirectives();
      await fetchData({ silent: true });
    }
  };

  // Fiche -> extra lie : onglet Extras & Credits, ligne surlignee.
  const handleOpenExtraFromDirective = (extraId) => {
    setDirectiveModal(null);
    setActiveTabState('extras');
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'extras');
    if (extraId) next.set('highlight', extraId);
    setSearchParams(next, { replace: true });
  };

  // Compte les pieces d'une directive, une fois (pas deux comptages en vol).
  const countDirectiveDocs = async (directiveId) => {
    if (!directiveId || countingRef?.current?.has(directiveId)) return;
    countingRef?.current?.add(directiveId);
    const n = await countDirectiveFiles(directiveId);
    countingRef?.current?.delete(directiveId);
    setDirectiveDocCounts((prev) => ({ ...prev, [directiveId]: n }));
  };

  // Repli seulement (lecture des compteurs en base echouee) : on ne compte que
  // les directives encore jamais comptees, au premier affichage de l'onglet.
  useEffect(() => {
    if (!docCountsFallback || activeTab !== 'directives') return;
    directives?.forEach((d) => {
      if (d?.id && !(d?.id in directiveDocCounts)) countDirectiveDocs(d?.id);
    });
  }, [docCountsFallback, activeTab, directives, directiveDocCounts]);

  const handleOpenDirectiveFromExtra = (directiveId) => {
    setActiveTab('directives');
    setHighlightDirectiveId(directiveId);
  };

  // ─── Lot D6 : filtres ────────────────────────────────────────────────────────
  // Les filtres vivent dans l'URL (source unique) : ils survivent au rechargement.
  const directiveFilters = {
    status: DIRECTIVE_STATUSES?.includes(searchParams?.get(DIRECTIVE_FILTER_PARAMS.status))
      ? searchParams?.get(DIRECTIVE_FILTER_PARAMS.status)
      : '',
    professional: searchParams?.get(DIRECTIVE_FILTER_PARAMS.professional) || '',
    division: searchParams?.get(DIRECTIVE_FILTER_PARAMS.division) || '',
    from: searchParams?.get(DIRECTIVE_FILTER_PARAMS.from) || '',
    to: searchParams?.get(DIRECTIVE_FILTER_PARAMS.to) || '',
  };

  // Fusion dans l'URL : les autres parametres (tab, tri, highlight) restent.
  const updateDirectiveFilters = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch || {})?.forEach(([key, value]) => {
      const param = DIRECTIVE_FILTER_PARAMS?.[key];
      if (!param) return;
      if (value) next.set(param, value);
      else next.delete(param);
    });
    setSearchParams(next, { replace: true });
  };

  const resetDirectiveFilters = () => {
    updateDirectiveFilters({ status: '', professional: '', division: '', from: '', to: '' });
  };

  // Recherche : nomenclature, titre, description, professionnel, division (valeur
  // et libelle traduit), numero.
  const matchesDirectiveSearch = (d) => {
    const q = directiveSearch?.toLowerCase()?.trim();
    if (!q) return true;
    return [
      d?.nomenclature, d?.title, d?.description, d?.division,
      d?.division ? divisionLabel(t, d?.division) : '',
      d?.professional?.company_name, String(d?.number ?? ''),
    ]?.some((v) => (v || '')?.toLowerCase()?.includes(q));
  };

  // date_directive est une date nue (YYYY-MM-DD) : comparaison de chaines.
  const matchesDirectivePeriod = (d) => {
    const day = d?.date_directive ? String(d?.date_directive)?.slice(0, 10) : '';
    if (directiveFilters?.from && (!day || day < directiveFilters?.from)) return false;
    if (directiveFilters?.to && (!day || day > directiveFilters?.to)) return false;
    return true;
  };

  const matchesDirectiveStatus = (d) => {
    const outcome = d?.outcome || 'pending';
    if (!directiveFilters?.status) return true;
    if (directiveFilters?.status === 'overdue') return isPendingOverdue(d);
    return outcome === directiveFilters?.status;
  };

  const filteredDirectives = (directives || [])?.filter((d) =>
    matchesDirectiveSearch(d)
    && (!directiveFilters?.professional || d?.professional_id === directiveFilters?.professional)
    && (!directiveFilters?.division || d?.division === directiveFilters?.division)
    && matchesDirectivePeriod(d)
    && matchesDirectiveStatus(d)
  );

  // Professionnels presents dans les directives du projet, pour le filtre.
  const directiveProfessionals = Object.values(
    (directives || [])?.reduce((acc, d) => {
      if (d?.professional_id && !acc[d?.professional_id]) {
        acc[d?.professional_id] = { id: d?.professional_id, company_name: d?.professional?.company_name || '—' };
      }
      return acc;
    }, {})
  )?.sort((a, b) => (a?.company_name || '')?.localeCompare(b?.company_name || ''));

  const handleSort = (key) => {
    if (!ALLOWED_SORT_KEYS?.includes(key)) return;
    const direction = sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    // Fusion : tab et highlight restent dans l'URL.
    const next = new URLSearchParams(searchParams);
    next.set('sort', key);
    next.set('dir', direction);
    setSearchParams(next, { replace: true });
  };

  const handleAddExtra = async (extraData) => {
    try {
      const { lines, pendingFiles, ...rest } = extraData;
      const created = await projectExtrasService?.create(rest);
      if (created) {
        // Save lines to project_extra_lines
        if (lines && lines?.length > 0) {
          const lineRows = lines?.map(l => {
            const isInternal = l?.subcontractId === '__internal__';
            const subcontractUUID = (!isInternal && l?.subcontractId) ? l?.subcontractId : null;
            console.log('Saving subcontract_id:', subcontractUUID, '| is_internal:', isInternal);
            return {
              extra_id: created?.id,
              subcontract_id: subcontractUUID,
              is_internal: isInternal,
              description: l?.description || null,
              amount: Number(l?.amount || 0)
            };
          });
          const { error: lineError } = await supabase?.from('project_extra_lines')?.insert(lineRows);
          if (lineError) console.error('Error saving extra lines:', lineError?.message);
        }
        // Pieces en attente : envoyees une fois l'extra cree (il faut son id),
        // chacune enregistree dans extra_documents.
        if (pendingFiles && pendingFiles?.length > 0) {
          for (const file of pendingFiles) {
            try {
              await uploadEntityDocument('extra', created?.id, file, id);
            } catch (uploadError) {
              console.error('Error uploading file:', uploadError?.message);
            }
          }
        }
        // Refresh from DB so all fields (division, co_number, etc.) appear immediately
        await fetchData({ silent: true });
      }
    } catch (err) {
      console.error('Error adding extra:', err);
    }
  };

  const handleStatusChange = async (extraId, newStatus) => {
    const extra = extras?.find(e => e?.id === extraId);

    // D) Guard: block leaving 'approved' if hasSignedAvenant is true
    if (extra?.status === 'approved' && extra?.hasSignedAvenant) {
      console.warn('handleStatusChange: blocked — extra has a signed avenant');
      return;
    }

    // Intercept approval: ALWAYS show cost-accepted modal first (pre-filled if already set)
    if (newStatus === 'approved') {
      const existingCost = parseFloat(extra?.costAccepted);
      // Pre-fill with existing costAccepted if present, else '0'
      setApprovalCost(existingCost > 0 ? String(existingCost) : '0');
      const base = parseFloat(extra?.costSubmitted ?? extra?.amount) || 0;
      const profit = parseFloat(extra?.profit) || 0;
      setApprovalModal({ extraId, previousStatus: extra?.status || 'draft', title: extra?.title, coNumber: extra?.coNumber, submittedCost: base + profit, type: extra?.type || 'extra' });
      return;
    }

    // For any non-approved status: update status in DB and local state
    if (newStatus !== 'approved') {
      try {
        // Via le service : l'ODC est remis a zero dans le meme UPDATE.
        try {
          await projectExtrasService?.updateStatus(extraId, newStatus);
        } catch (error) {
          console.error("STATUS UPDATE ERROR:", error);
          return;
        }
        // Optimistic UX update (temporary)
        setExtras(prev =>
          prev?.map(e =>
            e?.id === extraId
              ? { ...e, status: newStatus, coStatus: null, coReceivedAt: null, coReceivedBy: null }
              : e
          )
        );
        // Force DB sync — source of truth
        await fetchData({ silent: true });
      } catch (err) {
        console.error('Error updating status:', err);
      }
      return;
    }
  };

  // Opens ApproveExtraModal by fetching the extra's subcontractor lines
  const openApproveExtraModal = async (extra) => {
    try {
      const { data: rawLines } = await supabase
        ?.from('project_extra_lines')
        ?.select('id, subcontract_id, amount, description, is_internal')
        ?.eq('extra_id', extra?.id);

      // Resolve subcontractor names from the loaded subcontracts list
      const lines = (rawLines || [])?.map(l => {
        const found = subcontracts?.find(s => s?.id === l?.subcontract_id);
        return {
          ...l,
          subcontractorName:
            found?.subcontractor?.companyName ||
            found?.subcontractors?.company_name ||
            found?.name ||
            l?.subcontract_id,
        };
      });

      setApproveExtraModal({ extra, lines });
    } catch (err) {
      console.error('Error fetching lines for ApproveExtraModal:', err);
    }
  };

  const handleApproveExtraConfirm = async (selections, meta = {}) => {
    if (!approveExtraModal) return;
    setApproveExtraLoading(true);
    try {
      // Travail 100 % interne : aucun sous-traitant n'est engage, donc AUCUN
      // avenant a creer. Le saut est explicite — on ne compte pas sur le retour
      // anticipe de createAvenantsForExtra, qui sort en silence sur un tableau
      // vide et rendrait ce chemin invisible a la lecture.
      if (meta?.internalOnly) {
        await projectExtrasService?.updateStatus(
          approveExtraModal?.extra?.id,
          'approved'
        );
      } else {
        // Sequentiel et volontaire : si la creation des avenants leve, le
        // statut n'est PAS ecrit. Un extra approuve sans ses avenants serait
        // facturable au client sans engagement cote sous-traitant.
        await projectExtrasService?.createAvenantsForExtra(
          approveExtraModal?.extra?.id,
          selections
        );
        await projectExtrasService?.updateStatus(
          approveExtraModal?.extra?.id,
          'approved'
        );
      }
      // Full re-fetch so hasSignedAvenant is computed fresh from DB
      await fetchData({ silent: true });
      setApproveExtraModal(null);
    } catch (err) {
      console.error('Error confirming avenants:', err);
    } finally {
      setApproveExtraLoading(false);
    }
  };

  const handleApproveExtraCancel = () => {
    // Do NOT change status — extra stays as-is
    setApproveExtraModal(null);
  };

  const handleApprovalCancel = () => {
    setApprovalModal(null);
    setApprovalCost('0');
  };

  const handleApprovalConfirm = async () => {
    const cost = parseFloat(approvalCost);
    if (!cost || cost <= 0) return;
    setApprovalSaving(true);
    try {
      // Save costAccepted first
      const { error } = await projectExtrasService?.update(approvalModal?.extraId, {
        costAccepted: cost
      });
      if (error) {
        console.error("APPROVAL ERROR:", error);
        return;
      }
      // Re-fetch so the extra object has the latest data (including costAccepted)
      await fetchData({ silent: true });
      // Close cost modal, then open ApproveExtraModal with fresh extra data
      const freshExtra = { ...extras?.find(e => e?.id === approvalModal?.extraId), costAccepted: cost };
      setApprovalModal(null);
      await openApproveExtraModal(freshExtra);
    } catch (err) {
      console.error('Error confirming approval cost:', err);
    } finally {
      setApprovalSaving(false);
    }
  };

  const handleDelete = async (extraId) => {
    // C) Guard: refuse to delete an approved extra
    const extra = extras?.find(e => e?.id === extraId);
    if (extra?.status === 'approved') {
      console.warn('handleDelete: blocked — cannot delete an approved extra');
      return;
    }
    try {
      await projectExtrasService?.delete(extraId);
      await fetchData({ silent: true });
    } catch (err) {
      console.error('Error deleting extra:', err);
    }
  };

  // ─── Extra PDF Download ────────────────────────────────────────────────────
  const handleDownloadExtraPDF = async (extra) => {
    try {
      // Fetch org info + logo
      let company = '';
      let logoBase64 = null;
      let tagline = '';
      let orgAddress = '';
      let orgPhone = '';
      let orgEmail = '';
      let orgWebsite = '';
      let rbqNumber = '';
      let tpsNumber = '';
      let tvqNumber = '';

      const { data: { user } } = await supabase?.auth?.getUser();
      if (user?.id) {
        // Get organization_id from user profile
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('organization_id, full_name')
          ?.eq('id', user?.id)
          ?.single();

        const orgId = profile?.organization_id;

        if (orgId) {
          const { data: orgData } = await organizationService?.getOrgInfo(orgId);
          if (orgData) {
            company    = orgData?.name || profile?.full_name || '';
            tagline    = orgData?.tagline || '';
            orgAddress = orgData?.address || '';
            orgPhone   = orgData?.phone || '';
            orgEmail   = orgData?.email || '';
            orgWebsite = orgData?.website || '';
            rbqNumber  = orgData?.rbq_number || '';
            tpsNumber  = orgData?.tps_number || '';
            tvqNumber  = orgData?.tvq_number || '';

            if (orgData?.logo_url) {
              logoBase64 = await fetchOrgLogoAsBase64(supabase, orgData?.logo_url);
            }
          }
        } else {
          // Fallback: use profile name if no org
          company = profile?.full_name || '';
        }
      }

      // Fetch line items from project_extra_lines
      const { data: rawLines } = await supabase
        ?.from('project_extra_lines')
        ?.select('*')
        ?.eq('extra_id', extra?.id);

      const lines = (rawLines || [])?.map(l => {
        const internalLabel = orgName ? `${orgName} ${t('internal.suffix')}` : '';
        let subcontractName = 'All';
        if (l?.is_internal) {
          subcontractName = internalLabel;
        } else if (l?.subcontract_id) {
          const found = subcontracts?.find(s => s?.id === l?.subcontract_id);
          subcontractName =
            found?.subcontractor?.companyName ||
            found?.subcontractors?.company_name ||
            found?.name ||
            l?.subcontract_id;
        }
        return {
          subcontractName,
          amount: l?.amount,
          description: l?.description || ''
        };
      });

      // Client du projet, pour le bloc FACTURE A. Meme source que la page
      // Factures client : un echec ne doit pas empecher le PDF de sortir.
      let clients = [];
      try { clients = await listClientsForProject(id); } catch { clients = []; }

      // Professionnels du Repertoire : une zone a signer chacun, avant le client.
      let professionals = [];
      try { professionals = await listProfessionalsForProject(id); } catch { professionals = []; }

      const extraWithLines = {
        ...extra,
        lines
      };

      const blob = await pdf(
        <ExtraDocument
          extra={extraWithLines}
          project={project}
          clients={clients}
          professionals={professionals}
          company={company}
          logoBase64={logoBase64}
          language={language}
          projectName={project?.name}
          projectNumber={project?.projectNumber}
          projectAddress={[project?.address, project?.city, project?.state, project?.zipCode || project?.zip_code]?.filter(Boolean)?.join(', ')}
          pmFullName={project?.pm?.fullName || project?.pm?.full_name || ''}
          companyName={company}
          logoUrl={logoBase64}
          tagline={tagline}
          orgAddress={orgAddress}
          orgPhone={orgPhone}
          orgEmail={orgEmail}
          orgWebsite={orgWebsite}
          rbqNumber={rbqNumber}
          tpsNumber={tpsNumber}
          tvqNumber={tvqNumber}
        />
      )?.toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = `${extra?.coNumber || 'SOUMISSION'}_${(extra?.title || 'extra')?.replace(/\s+/g, '_')?.slice(0, 30)}.pdf`;
      a.download = filename;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Extra PDF generation error:', err);
    }
  };

  const handleEditExtra = async (updatedExtra) => {
    try {
      await fetchData({ silent: true });
    } catch (err) {
      console.error('Error updating extra:', err);
      throw err;
    }
  };

  // Read highlight param from URL (set by Change Orders page when navigating here)
  const highlightExtraId = searchParams?.get('highlight') || null;

  // Scroll to highlighted row after data loads
  useEffect(() => {
    if (highlightExtraId && !loading) {
      const el = document.getElementById(`extra-row-${highlightExtraId}`);
      if (el) {
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightExtraId, loading]);

  // Meme comportement cote Directives : on amene la ligne visee sous les yeux,
  // puis on relache la surbrillance pour qu'elle ne colle pas a la ligne.
  useEffect(() => {
    if (!highlightDirectiveId || activeTab !== 'directives') return;
    const el = document.getElementById(`directive-row-${highlightDirectiveId}`);
    if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHighlightDirectiveId(null), 3000);
    return () => clearTimeout(timer);
  }, [highlightDirectiveId, activeTab, directives]);

  const tabs = [
    { id: 'directives', label: t('extras.tabs.directives') },
    { id: 'extras', label: t('extras.tabs.extras') },
  ];

  const getSelectedRows = () => {
    return filteredExtras?.filter(e => selectedIds?.has(e?.id)) || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Icon name="Loader2" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="pt-0 pb-0 px-0 h-full flex flex-col">
      {/* Header Block with KPI Cards */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-6">
          {/* Left: Title + Breadcrumb */}
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Plus" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-gray-900">{t('extras.title')}</h1>
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.projectNumber || t('common.na')}
              </span>
              <span className="text-sm text-primary font-normal">-</span>
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.name || t('common.loading')}
              </span>
            </div>
            <p className="text-xs text-gray-500">{t('extras.subtitle')}</p>
          </div>

          {/* Right: KPI Cards */}
          <ExtrasKPICards
            extras={extras}
            constructionCosts={[]}
            generalCosts={[]}
            adminCosts={[]}
            extrasCosts={[]}
          />
        </div>
      </div>
      {/* ── Onglets ── */}
      <div className="mb-3">
        <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1 shadow-sm w-fit">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                activeTab === tab?.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {tab?.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'extras' && (
      <>
      {/* Search + Add Button Row */}
      <div className="flex items-center justify-between gap-3 mb-3">
        {/* Search Box + Status filter */}
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder={t('extras.toolbar.searchPlaceholder')}
              className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Icon name="X" size={12} />
              </button>
            )}
          </div>
          <StatusFilterDropdown
            options={extraStatusFilterOptions}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Export all (when nothing selected) */}
          {selectedIds?.size === 0 && filteredExtras?.length > 0 && (
            <div className="flex items-center gap-1">
              <button
                onClick={handleExportCSVFinal}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                title={t('extras.export.csvTooltip')}
              >
                <Icon name="FileSpreadsheet" size={13} className="text-green-500" />
                CSV
              </button>
              <button
                onClick={handleExportPDFFinal}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                title={t('extras.export.pdfTooltip')}
              >
                <Icon name="FileText" size={13} className="text-red-500" />
                PDF
              </button>
            </div>
          )}

          {/* Add Button */}
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm"
          >
            <Icon name="Plus" size={13} />
            {t('extras.toolbar.addExtraCredit')}
          </button>
        </div>
      </div>
      {/* Bulk Action Bar */}
      <BulkActionBar
        count={selectedIds?.size}
        actions={[
          {
            key: 'submit',
            label: t('bulk.submit'),
            icon: 'Send',
            variant: 'info',
            title: t('extras.bulk.submitTooltip'),
            onClick: () => handleBulkStatusChange('submitted'),
          },
          {
            key: 'status',
            label: t('bulk.changeStatus'),
            icon: 'RefreshCw',
            variant: 'default',
            dropdown: [
              {
                label: t('bulk.draft'),
                icon: 'FileEdit',
                onClick: () => handleBulkStatusChange('draft'),
              },
              {
                label: t('status.to_resubmit'),
                icon: 'RotateCcw',
                onClick: () => handleBulkStatusChange('to_resubmit'),
              },
              {
                label: t('bulk.reject'),
                icon: 'XCircle',
                variant: 'danger',
                onClick: () => handleBulkStatusChange('rejected'),
              },
            ],
          },
          {
            key: 'export',
            label: t('bulk.export'),
            icon: 'Download',
            variant: 'default',
            dropdown: [
              { label: t('bulk.exportCsv'), icon: 'FileSpreadsheet', onClick: () => handleExportCSVFinal?.() },
              { label: t('bulk.exportPdf'), icon: 'FileText',        onClick: () => handleExportPDFFinal?.() },
            ],
          },
        ]}
        onClear={handleClearSelection}
        clearLabel={t('bulk.clear')}
      />
      {/* Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
        {/* Table */}
        <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
          <ExtrasTable
            extras={filteredExtras}
            sortConfig={sortConfig}
            onSort={handleSort}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
            onEdit={(extra) => {
              // C) Guard: refuse to open edit mode if extra is approved
              if (extra?.status === 'approved') {
                console.warn('onEdit: blocked — cannot edit an approved extra');
                return;
              }
              setEditingStartsInView(false);
              setEditingExtra(extra);
            }}
            onView={(extra) => { setEditingStartsInView(true); setEditingExtra(extra); }}
            highlightExtraId={highlightExtraId}
            selectedIds={selectedIds}
            onToggleRow={handleToggleRow}
            onToggleAll={handleToggleAll}
            onDownloadPDF={handleDownloadExtraPDF}
            directiveMap={directiveMap}
            onOpenDirective={handleOpenDirectiveFromExtra}
            onCoChanged={() => fetchData({ silent: true })}
            onNotify={showDirectiveToast}
          />
        </div>
      </div>
      </>
      )}

      {activeTab === 'directives' && (
      <>
        {/* Regles metier (repliable, fermees par defaut) */}
        <DirectiveRulesHelp />

        {/* Barre d'outils Directives : recherche + bouton Filtres */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="relative flex-1 max-w-sm">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={directiveSearch}
              onChange={(e) => setDirectiveSearch(e?.target?.value)}
              placeholder={t('extras.directives.toolbar.searchPlaceholder')}
              className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
            {directiveSearch && (
              <button
                onClick={() => setDirectiveSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Icon name="X" size={12} />
              </button>
            )}
          </div>
            {/* Un seul bouton : statut, professionnel, division, periode, dans un menu */}
            <DirectiveFiltersMenu
              filters={directiveFilters}
              professionals={directiveProfessionals}
              onChange={updateDirectiveFilters}
              onReset={resetDirectiveFilters}
            />
            <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
              {t('extras.directives.filters.shown', { shown: filteredDirectives?.length || 0, total: directives?.length || 0 })}
            </span>
          </div>
          <button
            onClick={() => setDirectiveModal({ mode: 'create' })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm"
          >
            <Icon name="Plus" size={13} />
            {t('extras.directives.toolbar.newDirective')}
          </button>
        </div>


        {/* Registre */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
          <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
            {directives?.length > 0 && filteredDirectives?.length === 0 ? (
              <div className="p-10 text-center">
                <Icon name="Filter" size={28} className="mx-auto mb-2 text-gray-300" />
                <p className="text-xs text-gray-500">{t('extras.directives.filters.noResults')}</p>
              </div>
            ) : (
            <DirectivesTable
              directives={filteredDirectives}
              onCreateExtra={(d) => handleDirectiveOutcome(d, 'extra')}
              onCreateCredit={(d) => handleDirectiveOutcome(d, 'credit')}
              onNoImpact={(d) => handleDirectiveOutcome(d, 'no_impact')}
              onOpenDocuments={(d) => setDirectiveDocs(d)}
              busyId={directiveBusyId}
              documentCounts={directiveDocCounts}
              countsComplete={docCountsFromDb}
              highlightId={highlightDirectiveId}
              canDelete={canDeleteDirective}
              onEdit={(d) => setDirectiveModal({ mode: 'edit', id: d?.id })}
              onChangeOutcome={(d, outcome) => setOutcomeChange({ directive: d, outcome })}
              onHistory={(d) => setHistoryDirective(d)}
              onDelete={handleRequestDelete}
              onOpenDetail={(d) => setDirectiveModal({ mode: 'view', id: d?.id })}
              onLinkExtra={(d) => setLinkingDirective(d)}
              onUnlinkExtra={(d) => setUnlinkingDirective(d)}
              onRevertExtra={handleRequestRevertExtra}
            />
            )}
          </div>
        </div>
      </>
      )}

      {/* Directive : consulter / modifier / creer, une seule modale — montee AVANT
          les autres modales de directive (historique complet, pieces,
          confirmations), qui s'ouvrent donc par-dessus elle. */}
      {directiveModal && (() => {
        const current = directiveModal?.mode === 'create'
          ? null
          : directives?.find((d) => d?.id === directiveModal?.id) || null;
        if (directiveModal?.mode !== 'create' && !current) return null;
        return (
          <DirectiveModal
            key={directiveModal?.mode === 'create' ? 'create' : current?.id}
            mode={directiveModal?.mode}
            directive={current}
            directives={directives}
            extra={current?.extra_id ? extras?.find((e) => e?.id === current?.extra_id) || null : null}
            project={project}
            projectId={id}
            onClose={() => setDirectiveModal(null)}
            onCreate={handleCreateDirective}
            onUpdate={handleUpdateDirective}
            onDocumentsChanged={(count) => {
              // Comme la modale Pieces : le compteur de la ligne suit.
              if (current?.id) setDirectiveDocCounts((prev) => ({ ...prev, [current?.id]: count }));
            }}
            onFullHistory={(d) => setHistoryDirective(d)}
            onOpenExtra={handleOpenExtraFromDirective}
            onLinkExtra={(d) => setLinkingDirective(d)}
            onUnlinkExtra={(d) => setUnlinkingDirective(d)}
            onOpenRfis={() => navigate(`/project/${id}/rfi`)}
          />
        );
      })()}

      {/* Relier une directive a un extra existant (par-dessus la fiche) */}
      <LinkExtraModal
        directive={linkingDirective}
        projectId={id}
        onClose={() => setLinkingDirective(null)}
        onLinked={handleExtraLinked}
      />

      {/* Delier l'extra : confirmation, l'extra est conserve */}
      <ConfirmDialog
        isOpen={!!unlinkingDirective}
        title={t('extras.directives.link.unlinkTitle')}
        message={t('extras.directives.link.unlinkMessage', {
          number: unlinkingDirective?.extra?.co_number || t('extras.directives.outcomeChange.linkedExtra'),
        })}
        icon="Unlink"
        confirmLabel={t('extras.directives.link.unlinkConfirm')}
        loading={unlinkSaving}
        onConfirm={handleConfirmUnlink}
        onCancel={() => setUnlinkingDirective(null)}
      />

      {/* Revenir a « A evaluer » d'une directive liee (lot D9) : suppression de
          l'extra (brouillon : confirmation courte ; soumis / rejete :
          avertissement) ou blocage (approuve, lie a de l'argent : Fermer). */}
      <ConfirmDialog
        isOpen={!!revertExtra}
        title={revertDialog?.title}
        message={revertDialog?.message}
        icon={revertExtra?.check?.mode === 'blocked' ? 'Lock' : 'AlertTriangle'}
        confirmVariant={revertExtra?.check?.mode === 'blocked' ? 'primary' : 'danger'}
        confirmOnly={revertExtra?.check?.mode === 'blocked'}
        confirmLabel={revertExtra?.check?.mode === 'blocked' ? t('extras.directives.issue.close') : revertDialog?.confirmLabel}
        loading={revertSaving}
        onConfirm={handleConfirmRevertExtra}
        onCancel={() => setRevertExtra(null)}
      />

      {/* Changer l'issue : l'effet est explique avant d'ecrire */}
      <ConfirmDialog
        isOpen={!!outcomeChange}
        title={t('extras.directives.outcomeChange.title')}
        message={outcomeChange ? outcomeChangeMessage(outcomeChange) : ''}
        icon="ArrowRightLeft"
        confirmLabel={t('extras.directives.outcomeChange.confirm')}
        loading={outcomeSaving}
        onConfirm={handleConfirmOutcomeChange}
        onCancel={() => setOutcomeChange(null)}
      />

      {/* Supprimer une directive (admin) */}
      <ConfirmDialog
        isOpen={!!deletingDirective}
        title={t('extras.directives.delete.title')}
        message={t('extras.directives.delete.message', { name: deletingDirective?.nomenclature || deletingDirective?.title || '—' })}
        confirmVariant="danger"
        confirmLabel={t('extras.directives.delete.confirm')}
        loading={deleteSaving}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeletingDirective(null)}
      />

      {/* Historique d'une directive */}
      <DirectiveHistoryModal directive={historyDirective} onClose={() => setHistoryDirective(null)} />

      {/* Pieces jointes d'une directive — modale dediee, lue dans
          directive_documents ; objets dans contracts-documents/directives/{id} */}
      <DirectiveDocumentsModal
        isOpen={!!directiveDocs}
        onClose={() => setDirectiveDocs(null)}
        directive={directiveDocs}
        project={project}
        subtitle={
          directiveDocs
            ? `${directiveDocs?.nomenclature || ''}${directiveDocs?.number != null ? ` · ${t('extras.directives.documentsSubtitle', { number: directiveDocs?.number })}` : ''}`
            : ''
        }
        onCountChange={(count) => {
          // Seule cette directive change, sans relecture.
          const directiveId = directiveDocs?.id;
          if (directiveId) setDirectiveDocCounts((prev) => ({ ...prev, [directiveId]: count }));
        }}
      />

      {/* Retour des actions sur une directive */}
      {directiveToast && (
        <div
          className={`fixed top-4 right-4 z-[100] flex items-center gap-2 px-4 py-3 text-white text-sm font-medium rounded-xl shadow-lg ${
            directiveToast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'
          }`}
        >
          <Icon name={directiveToast?.type === 'error' ? 'AlertCircle' : 'CheckCircle2'} size={16} />
          {directiveToast?.text}
        </div>
      )}

      {/* Add Extra Modal */}
      {showAddModal && (
        <AddExtraModal
          projectId={id}
          subcontracts={subcontracts}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddExtra}
        />
      )}
      {/* Edit Extra Modal */}
      {editingExtra && (
        <AddExtraModal
          projectId={id}
          subcontracts={subcontracts}
          onClose={() => setEditingExtra(null)}
          onSave={handleEditExtra}
          isEditMode={true}
          extra={editingExtra}
          startInView={editingStartsInView}
        />
      )}
      {/* Approval Cost Modal */}
      {approvalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Icon name="CircleDollarSign" size={18} className="text-primary" />
                <h2 className="text-sm font-semibold text-gray-900">
                  {approvalModal?.type === 'credit'
                    ? t('extra.enterAcceptedCostTitleCredit')
                    : t('extra.enterAcceptedCostTitle')}
                </h2>
              </div>
              <button
                onClick={handleApprovalCancel}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            {/* Body */}
            <div className="px-5 py-5 space-y-4">
              {/* Context info block */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs text-gray-500 shrink-0">
                    {approvalModal?.type === 'credit' ? t('extra.creditLabel') : t('extra.extraLabel')}:
                  </span>
                  <span className="text-xs font-semibold text-gray-900 text-right">{approvalModal?.title || '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500 shrink-0">{t('extra.coLabel')}:</span>
                  <span className="text-xs font-semibold text-gray-900">{approvalModal?.coNumber || '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500 shrink-0">{t('extra.submittedCost')}:</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {fmtCAD(approvalModal?.submittedCost)}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('extra.acceptedCost')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">$</span>
                  <input
                    type="number"
                    value={approvalCost}
                    onChange={e => setApprovalCost(e?.target?.value)}
                    onFocus={(e) => e?.target?.select()}
                    onClick={(e) => e?.target?.select()}
                    min="0.01"
                    step="0.01"
                    autoFocus
                    className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
                  />
                </div>
                {approvalCost !== '' && !(parseFloat(approvalCost) > 0) && (
                  <p className="mt-1 text-xs text-red-500">{t('extra.errorGreaterThanZero')}</p>
                )}
              </div>
            </div>
            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={handleApprovalCancel}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleApprovalConfirm}
                disabled={approvalSaving || !approvalCost || !(parseFloat(approvalCost) > 0)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
              >
                {approvalSaving ? <Icon name="Loader2" size={12} className="animate-spin" /> : <Icon name="Check" size={12} />}
                {approvalSaving ? t('common.saving') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ApproveExtraModal — grouped subcontractor lines confirmation */}
      {approveExtraModal && (
        <ApproveExtraModal
          isOpen={!!approveExtraModal}
          extra={approveExtraModal?.extra}
          lines={approveExtraModal?.lines}
          onCancel={handleApproveExtraCancel}
          onConfirm={handleApproveExtraConfirm}
          loading={approveExtraLoading}
        />
      )}
      {/* Action Feedback Toast */}
      <ActionFeedbackToast feedback={actionFeedback} />
    </div>
  );
};

export default ExtrasPage;